import { isoWeekStart, type IsoDate } from '../dates';
import { canTakeMeatAddOn } from '../diet';
import type { Member } from '../household';
import type { MeatAddOn } from '../plan';
import { productOf } from '../price-book';
import type { Recipe } from '../recipe';
import type { Product } from '../types';

/**
 * DE: Zähler für Fleischbeilagen pro Woche und Person.
 * EN: Counter of meat add-ons per week and member.
 */
export type AddOnCounts = Map<string, number>;

const key = (date: IsoDate, memberId: string) => `${isoWeekStart(date)}|${memberId}`;

/**
 * DE: Personen, die in dieser Woche noch Fleischbeilagen wollen und sie zu diesem Rezept bekommen können.
 * EN: Members who still want meat add-ons this week and can get one with this recipe.
 */
export function addOnTakers(
  recipe: Recipe,
  date: IsoDate,
  members: readonly Member[],
  context: { catalog: ReadonlyMap<string, Product>; counts: AddOnCounts },
): Member[] {
  if (!recipe.meatAddOn) return [];
  const product = productOf(context.catalog, recipe.meatAddOn.productId);
  return members.filter(
    (member) =>
      (context.counts.get(key(date, member.id)) ?? 0) < member.meatAddOnsPerWeek &&
      canTakeMeatAddOn(member, product),
  );
}

/**
 * DE: Fleischbeilagen für Abendessen und Mittagessen (doppelte Portion) anlegen und zählen.
 * EN: Create and count meat add-ons for dinner and lunch (double portion).
 */
export function assignAddOns(
  recipe: Recipe,
  date: IsoDate,
  takers: readonly Member[],
  counts: AddOnCounts,
) {
  const addOn = recipe.meatAddOn;
  if (!addOn) return [];
  const addOns = takers.map((member): MeatAddOn => ({
    memberId: member.id,
    productId: addOn.productId,
    quantity: addOn.quantityPerPortion * member.portionFactor * 2,
  }));
  recordAddOns(date, addOns, counts);
  return addOns;
}

/**
 * DE: Bereits geplante Fleischbeilagen in ihrer Woche zählen.
 * EN: Count already planned meat add-ons in their week.
 */
export function recordAddOns(
  date: IsoDate,
  addOns: readonly MeatAddOn[],
  counts: AddOnCounts,
): void {
  for (const { memberId } of addOns) {
    counts.set(key(date, memberId), (counts.get(key(date, memberId)) ?? 0) + 1);
  }
}
